/**
 * Analytics Context
 * State management for the YYZ Prep Robust Analytics Engine
 */

import React, { createContext, useState, useContext, useCallback } from 'react';

const API_URL = import.meta.env.VITE_API_URL || 'https://react-app-production-3114.up.railway.app';

const AnalyticsContext = createContext();

export const AnalyticsProvider = ({ children }) => {
  // Dashboard state
  const [dashboardData, setDashboardData] = useState(null);
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [dashboardError, setDashboardError] = useState(null);

  // Selected month for drill-down
  const [selectedMonth, setSelectedMonth] = useState(null);

  // Monthly drill-down state
  const [monthlyData, setMonthlyData] = useState(null);
  const [monthlyLoading, setMonthlyLoading] = useState(false);
  const [monthlyError, setMonthlyError] = useState(null);

  // Integrity Hub state
  const [integrityData, setIntegrityData] = useState(null);
  const [integrityLoading, setIntegrityLoading] = useState(false);
  const [integrityError, setIntegrityError] = useState(null);

  // Service mappings state
  const [serviceMappings, setServiceMappings] = useState([]);

  /**
   * Fetch 12-month dashboard data
   */
  const fetchDashboard = useCallback(async () => {
    setDashboardLoading(true);
    setDashboardError(null);
    try {
      const response = await fetch(`${API_URL}/api/analytics/dashboard`);
      if (!response.ok) throw new Error('Failed to fetch dashboard data');
      const data = await response.json();
      setDashboardData(data);
    } catch (error) {
      console.error('Dashboard fetch error:', error);
      setDashboardError(error.message);
    } finally {
      setDashboardLoading(false);
    }
  }, []);

  /**
   * Fetch monthly drill-down data
   */
  const fetchMonthlyData = useCallback(async (year, month) => {
    setMonthlyLoading(true);
    setMonthlyError(null);
    try {
      const response = await fetch(`${API_URL}/api/analytics/monthly/${year}/${month}`);
      if (!response.ok) throw new Error('Failed to fetch monthly data');
      const data = await response.json();
      setMonthlyData(data);
      setSelectedMonth({ year, month });
    } catch (error) {
      console.error('Monthly fetch error:', error);
      setMonthlyError(error.message);
    } finally {
      setMonthlyLoading(false);
    }
  }, []);

  /**
   * Fetch integrity hub data
   */
  const fetchIntegrity = useCallback(async () => {
    setIntegrityLoading(true);
    setIntegrityError(null);
    try {
      const response = await fetch(`${API_URL}/api/analytics/integrity`);
      if (!response.ok) throw new Error('Failed to fetch integrity data');
      const data = await response.json();
      setIntegrityData(data);
    } catch (error) {
      console.error('Integrity fetch error:', error);
      setIntegrityError(error.message);
    } finally {
      setIntegrityLoading(false);
    }
  }, []);

  /**
   * Fetch service mappings
   */
  const fetchServiceMappings = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/analytics/service-mappings`);
      if (!response.ok) throw new Error('Failed to fetch service mappings');
      const data = await response.json();
      setServiceMappings(data);
    } catch (error) {
      console.error('Service mappings fetch error:', error);
    }
  }, []);

  /**
   * Create a new service mapping
   */
  const createServiceMapping = useCallback(async (keywordMatch, category, isProfitBearing) => {
    try {
      const response = await fetch(`${API_URL}/api/analytics/service-mappings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ keywordMatch, category, isProfitBearing })
      });
      if (!response.ok) throw new Error('Failed to create service mapping');
      const data = await response.json();
      setServiceMappings(prev => [...prev, data]);
      return data;
    } catch (error) {
      console.error('Create mapping error:', error);
      throw error;
    }
  }, []);

  /**
   * Resolve an audit queue item
   */
  const resolveAuditItem = useCallback(async (id) => {
    try {
      const response = await fetch(`${API_URL}/api/analytics/audit-queue/${id}/resolve`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ resolvedBy: 'user' })
      });
      if (!response.ok) throw new Error('Failed to resolve audit item');
      // Refresh integrity data after resolving
      await fetchIntegrity();
    } catch (error) {
      console.error('Resolve audit error:', error);
      throw error;
    }
  }, [fetchIntegrity]);

  /**
   * Link a reimbursement to a Square invoice
   */
  const linkReimbursement = useCallback(async (id, squareInvoiceId) => {
    try {
      const response = await fetch(`${API_URL}/api/analytics/reimbursements/${id}/link`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ squareInvoiceId })
      });
      if (!response.ok) throw new Error('Failed to link reimbursement');
      // Refresh integrity data after linking
      await fetchIntegrity();
    } catch (error) {
      console.error('Link reimbursement error:', error);
      throw error;
    }
  }, [fetchIntegrity]);

  /**
   * Format currency
   */
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  /**
   * Format percentage
   */
  const formatPercentage = (value) => {
    return `${Math.round(value)}%`;
  };

  const value = {
    // Dashboard
    dashboardData,
    dashboardLoading,
    dashboardError,
    fetchDashboard,

    // Month selection
    selectedMonth,
    setSelectedMonth,

    // Monthly drill-down
    monthlyData,
    monthlyLoading,
    monthlyError,
    fetchMonthlyData,

    // Integrity Hub
    integrityData,
    integrityLoading,
    integrityError,
    fetchIntegrity,

    // Service mappings
    serviceMappings,
    fetchServiceMappings,
    createServiceMapping,

    // Actions
    resolveAuditItem,
    linkReimbursement,

    // Utilities
    formatCurrency,
    formatPercentage
  };

  return (
    <AnalyticsContext.Provider value={value}>
      {children}
    </AnalyticsContext.Provider>
  );
};

export const useAnalytics = () => {
  const context = useContext(AnalyticsContext);
  if (!context) {
    throw new Error('useAnalytics must be used within an AnalyticsProvider');
  }
  return context;
};

export default AnalyticsContext;
